import { Alert, SimpleGrid, Text, Textarea, TextInput } from "@mantine/core";
import { SectionCard } from "../HomeBlockShared";
import { DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import { NumbersConfig } from "../homeBlockConfig";
import {
  Crop,
  EditableList,
  EditorLayout,
  fieldSetter,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

const IMG = DESIGN_IMAGE.numbers;

const COL_X = [27, 346, 654, 963];

const SAMPLE_STATS = [
  ["25+", "NĂM KINH NGHIỆM", "Đồng hành cùng người Việt từ năm 2001", "/gioi-thieu"],
  ["20+", "SHOWROOM", "Trải dài từ Bắc vào Nam", "/he-thong-showroom"],
  ["1.000.000+", "KHÁCH HÀNG", "Tin tưởng lựa chọn HACOM", "/khach-hang"],
  ["500+", "THƯƠNG HIỆU", "Đối tác công nghệ toàn cầu", "/thuong-hieu"],
  ["50.000+", "SẢN PHẨM", "Từ Laptop tới AI Workstation", "/san-pham"],
  ["100+", "CHUYÊN GIA", "Tư vấn giải pháp công nghệ", "/lien-he"],
  ["24/7", "HỖ TRỢ", "Đồng hành cùng khách hàng", "/ho-tro-khach-hang"],
  ["10.000+", "ĐƠN HÀNG / THÁNG", "Hệ thống vận hành toàn quốc", "/gioi-thieu"],
];

// Ảnh mẫu cắt từ thiết kế: chỉ để xem khi chưa tải ảnh, không lưu
const SAMPLE_CROPS = new Map<string, Crop>(
  SAMPLE_STATS.map((_, i): [string, Crop] => [
    `stat-${i + 1}`,
    { x: COL_X[i % 4], y: i < 4 ? 255 : 434, w: 105, h: 150 },
  ])
);

const initialConfig = (block: HomeBlock): NumbersConfig => {
  const sample = block.code === "home-numbers";
  return {
    heading: {
      title: sample ? "HACOM IN NUMBERS" : block.name,
      subtitle: sample ? "NHỮNG CON SỐ TẠO NÊN NIỀM TIN TỪ HACOM" : "",
      description: sample
        ? "Hơn 25 năm đồng hành cùng người Việt, HACOM không ngừng kiến tạo giá trị, mang công nghệ đến gần hơn với mọi người."
        : "",
    },
    stats: sample
      ? SAMPLE_STATS.map(([value, label, description, link], i) => ({
          id: `stat-${i + 1}`,
          value,
          label,
          description,
          link,
          image: null,
        }))
      : [],
  };
};

const EditNumbers = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const { heading } = config;
  const patchHeading = (v: Partial<NumbersConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));

  return (
    <EditorLayout block={block}>
      <SectionCard title="Đầu block">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Tiêu đề"
            value={heading.title}
            onChange={(e) => patchHeading({ title: e.currentTarget.value })}
          />
          <TextInput
            label="Phụ đề"
            value={heading.subtitle}
            onChange={(e) => patchHeading({ subtitle: e.currentTarget.value })}
          />
        </SimpleGrid>
        <Textarea
          label="Mô tả"
          autosize
          minRows={2}
          value={heading.description}
          onChange={(e) => patchHeading({ description: e.currentTarget.value })}
        />
      </SectionCard>

      <SectionCard title="Ô số liệu">
        <Alert color="orange" variant="light" radius="sm" p={10}>
          <Text fz={13}>
            Chỉ nhập số liệu đã được xác nhận. Số trong dữ liệu mẫu lấy từ bản
            thiết kế, cần kiểm tra lại trước khi lưu.
          </Text>
        </Alert>
        <EditableList
          items={config.stats}
          onChange={fieldSetter(setConfig, "stats")}
          itemLabel={(s) => `${s.value} ${s.label}`.trim()}
          columns={[
            { label: "Ảnh", width: "56px" },
            { label: "Con số", width: "120px" },
            { label: "Nhãn", width: "minmax(0, 1fr)" },
            { label: "Mô tả", width: "minmax(0, 1.4fr)" },
            { label: "Link", width: "minmax(0, 0.8fr)" },
          ]}
          renderCells={(s, update) => (
            <>
              <ThumbPicker
                label={s.label}
                src={s.image}
                imageId={IMG}
                crop={SAMPLE_CROPS.get(s.id)}
                onPick={(url) => update({ image: url })}
              />
              <TextInput
                aria-label="Con số"
                value={s.value}
                onChange={(e) => update({ value: e.currentTarget.value })}
              />
              <TextInput
                aria-label="Nhãn"
                value={s.label}
                onChange={(e) => update({ label: e.currentTarget.value })}
              />
              <TextInput
                aria-label={`Mô tả ${s.label}`}
                value={s.description}
                onChange={(e) => update({ description: e.currentTarget.value })}
              />
              <TextInput
                aria-label={`Link ${s.label}`}
                value={s.link}
                onChange={(e) => update({ link: e.currentTarget.value })}
              />
            </>
          )}
          newItem={() => ({ id: newId(), value: "", label: "", description: "", link: "", image: null })}
          addLabel="Thêm ô số"
          max={8}
          minWidth={760}
        />
      </SectionCard>
    </EditorLayout>
  );
};

export default EditNumbers;
